// @flow

import React from 'react';
import ReactDOM from 'react-dom';

import SettingsRuntime from './settings-runtime';
import * as FitbitComponents from './fitbit-components';
import SettingsComponent from './lib/settings-component';
import createComponent from './create-component';
import requireImpl from './requireImpl';

import { setLoadReason } from './lib/load-reason';

class Settings {
  static _instance: * = null;

  _defaults = {};

  _settingsPage: * = null;
  _shouldRender = true;

  static init(shouldRender = true, defaults): Settings {
    if (Settings._instance) {
      return Settings._instance;
    }

    const runtime = new Settings();
    Settings._instance = runtime;
    runtime._shouldRender = shouldRender;
    runtime._defaults = defaults;
    runtime._defaults = {
      ...defaults
    };

    return Settings._instance;
  }

  updateDefaults(defaults: *) {
    if (!Settings._instance) {
      Settings.init(false, defaults);
      return;
    }

    Settings._instance._defaults = {
      ...Settings._instance._defaults,
      ...defaults
    };

    Settings._instance.render();
  }

  setSettingsPage(settingsPage: *) {
    this._settingsPage = settingsPage;
  }

  onRegister() {
    if (!this._shouldRender) {
      return;
    }

    this.render();
  }

  render() {
    ReactDOM.render(
      <SettingsRuntime settingsPage={this._settingsPage} {...this._defaults} />,
      document.getElementById('root')
    );
  }
}

export default Settings;

const registerSettingsPage = (settingsPage: *): void => {
  const instance = Settings.init();
  instance.setSettingsPage(settingsPage);
  instance.onRegister();
};

const componentNames = Object.keys(FitbitComponents).reduce((names, key) => {
  names[key] = key;
  return names;
}, {});

const SettingsContext = {
  createComponent,
  registerSettingsPage,
  SettingsComponent,
  ...componentNames
};

// Avoid starting app until postMessage is set
window.__webview_comms__.on('ready', async () => {
  let { settingsData } = await window.__webview_comms__.call({ event: 'GET:settings-data' });

  if (typeof settingsData === 'string') {
    settingsData = JSON.parse(settingsData);
  }

  Settings.init(undefined, {
    settingsData: settingsData,
    notifyChange: (onNotify: *) => {
      window.__webview_comms__.on('NOTIFY:settings-change', ({ changeType, key, oldValue, newValue } = {}) => {
        onNotify(changeType, {
          key,
          oldValue,
          newValue
        });
      });
    },
    triggerChange: (changeType: String, event: *) => {
      event = event || {};
      const { key } = event;
      let { oldValue, newValue } = event;
      window.__webview_comms__.call({
        event: 'TRIGGER:settings-change',
        data: {
          changeType,
          key,
          oldValue,
          newValue
        }
      });
    }
  });

  window.__webview_comms__.call({ event: 'TRIGGER:settings-runtime-ready' });
});

window.__webview_comms__.onNotify('load-reason', (loadReason) => {
  setLoadReason(loadReason);
});

window.__webview_comms__.onLoad('settings-code', async ({ settingsData, settingsCode }) => {
  if (typeof settingsData === 'string') {
    settingsData = JSON.parse(settingsData);
  }

  if (settingsData) {
    Settings._instance.updateDefaults({ settingsData });
  }

  // Allowed globals
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects
  // eslint-disable-next-line
  const globals = new Set([
    // Value properties
    'Infinity',
    'NaN',
    'undefined',

    // Global functions
    'isFinite',
    'isNaN',
    'parseFloat',
    'parseInt',
    'encodeURIComponent',
    'decodeURIComponent',

    // Core objects
    'Object',
    'Array',
    'Boolean',
    'Symbol',
    'String',
    'Number',
    'Date',
    'RegExp',

    // Error objects
    'Error',
    'RangeError',
    'ReferenceError',
    'TypeError',
    'URIError',

    // Useful ES6 types
    'Promise',
    'Map',
    'Set',

    // Fetch API
    'fetch',
    'Headers',
    'Request',
    'Response',

    // Other
    'console',
    'JSON',
    'Math',
    'setTimeout',
    'clearTimeout',
    'setInterval',
    'clearInterval',
    'require'
  ]);

  const userSettings = await window.__webview_comms__.call({ event: 'GET:user-settings' });
  SettingsContext.require = requireImpl({ userSettings });

  // Generate name=value pairs to copy context into local vars
  const contextVars = Object.keys(SettingsContext)
    .map((key) => `${key}=context.${key}`);

  // Rough test to see if a name looks like a valid JS identifier
  const VALID_IDENTIFIER_REGEX = /^[$_A-Z]\w*$/i;

  // Get list of globals that are safe to shadow with variables
  const shadowGlobals = Object.getOwnPropertyNames(window)
    .filter((key) =>
      VALID_IDENTIFIER_REGEX.test(key) && !globals.has(key));

  /* Disable sandbox protection for now
  var clonedContext = Object.assign({}, context);

  var sandbox = new Proxy(clonedContext, {
    get: function (target, key, receiver) {
      if (key === Symbol.unscopables) {
        return undefined;
      }
      return Reflect.get(target, key, receiver);
    },
    has: function (target, key) {
      if (globals.includes(key)) {
        return Reflect.has(target, key);
      }
      return true;
    }
  });
  */

  // Blocks access to window and only allows the above globals
  // This is only intended to prevent accidental use of
  // browser-specific objects, and is not meant as a
  // security mechanism.
  // eslint-disable-next-line
  const fn = new Function('context', `
    // Shadow globals to prevent accidental use
    var ${shadowGlobals.join(',')};

    // Make context available as local variables
    var ${contextVars.join(',')};

    // Hide context
    context = undefined;

    // Wrap settings code in function so 'use strict' is preserved
    (function () {
      ${settingsCode}
    }).call({}); // prevent 'this' from being 'window' in non-strict mode
  `);

  setTimeout(() => fn(SettingsContext), 0);
});



// WEBPACK FOOTER //
// ./src/index.js